import React, { useState, useEffect } from 'react';
import { Button, Container, Stack, Form, Row, Col, Accordion, Card } from 'react-bootstrap';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSaveJobQuestionnaireMutation, useSaveNewJobMutation } from 'services';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { setModalConfig } from 'store/common';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';

interface Question {
	id: number;
	question: string;
	options: string[];
}

interface CustomToggleProps {
	eventKey: string;
	children: React.ReactNode;
}

const CustomToggle: React.FC<CustomToggleProps> = ({ eventKey, children }) => {
	const decoratedOnClick = useAccordionButton(eventKey);
	return (
		<Button className="edit-btn border-0 rounded-circle p-0" onClick={decoratedOnClick}>
			{children}
		</Button>
	);
};

const CreateQuestionnaire: React.FC = () => {
	const { translate } = useTranslate();
	const location = useLocation();
	const { formValues } = location.state;
	const { id } = useParams<any>();
	const navigate = useNavigate();

	if (!id && Object?.keys(formValues)?.length <= 1) navigate(-1);

	const [questions, setQuestions] = useState<Question[]>([
		{ id: Date.now(), question: '', options: ['', '', '', ''] },
	]);

	const { mutateAsync: saveQuestionnaire } = useSaveJobQuestionnaireMutation();
	const { mutateAsync: saveNewJobMutation } = useSaveNewJobMutation();

	const initialValues = {
		questions: questions.map((ques) => ({
			question: ques.question,
			options: ques.options,
		})),
	};

	const validationSchema = Yup.object().shape({
		questions: Yup.array().of(
			Yup.object().shape({
				question: Yup.string()
					.required(
						translate('Validation.required', { field: translate('Questionnaire.question') }),
					)
					.trim(),
				options: Yup.array()
					.of(
						Yup.string()
							.required(translate('Validation.required', { field: translate('Extras.option') }))
							.trim(),
					)
					.min(4, translate('Extras.addAllOptions')),
			}),
		),
	});

	const formik: any = useFormik({
		initialValues,
		validationSchema,
		onSubmit: async (values) => {
			const updatedValue: any = values.questions.map(({ id, ...newObj }) => newObj); // Destructure to omit `id`
			try {
				const jobResult = await saveNewJobMutation({ ...formValues });
				if (jobResult?.success) {
					const questionnaireResult = await saveQuestionnaire({
						totalQuestion: updatedValue.length,
						jobId: jobResult.data.jobId,
						questionArray: updatedValue,
					});
					if (questionnaireResult?.success) {
						formik.resetForm();
						setModalConfig({
							visible: true,
							type: 'successModal',
						});
					}
				}
			} catch (error) {
				console.error('Error saving the questionnaire:', error);
				setModalConfig({
					visible: true,
					type: 'errorModal',
					message: translate('Extras.QuestionnaireTryAgain'),
				});
			}
		},
	});

	useEffect(() => {
		formik.validateForm();
	}, []);

	const addQuestion = () => {
		if (formik.isValid && questions.length < 10) {
			const newId = Date.now();
			const newQuestion: Question = { id: newId, question: '', options: ['', '', '', ''] };
			setQuestions([...questions, newQuestion]);
			formik.setValues({
				...formik.values,
				questions: [...formik.values.questions, newQuestion],
			});
		}
	};

	const deleteQuestion = (id: number) => {
		if (questions.length > 1) {
			const updatedQuestions = questions.filter((ques) => ques.id !== id);
			setQuestions(updatedQuestions);
			formik.setValues({
				...formik.values,
				questions: updatedQuestions,
			});
		}
	};

	const handleQuestionChange = (index: number, value: string) => {
		const updatedQuestions = [...questions];
		updatedQuestions[index].question = value;
		setQuestions(updatedQuestions);
		formik.setFieldValue(`questions.${index}.question`, value);
	};

	const handleOptionChange = (questionIndex: number, optionIndex: number, value: string) => {
		const updatedQuestions = [...questions];
		updatedQuestions[questionIndex].options[optionIndex] = value;
		setQuestions(updatedQuestions);
		formik.setFieldValue(`questions.${questionIndex}.options.${optionIndex}`, value);
	};

	return (
		<section className="create-questionnaire py-50">
			<Container>
				<Form onSubmit={formik.handleSubmit}>
					<Stack className="create-questionnaire-inner">
						<div className="create-questionnaire-head d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
							<div>
								<h4 className="fw-bold">{translate('Questionnaire.createQuestionnaire')}</h4>
								<p className="mb-0">{translate('Questionnaire.addQuestions')}</p>
							</div>
						</div>
						<Accordion defaultActiveKey="0" className="create-questionnaire-box">
							{questions?.map((ques, index) => (
								<Card key={ques?.id}>
									<Card.Header>
										<div className="d-flex gap-2 gap-lg-3 w-100">
											<div className="create-questionnaire-question flex-1">
												<Form.Group className="position-relative">
													<Form.Control
														type="text"
														placeholder={translate('Extras.writeQuestion')}
														name={`questions.${index}.question`}
														className="rounded-pill"
														autoComplete="off"
														value={formik?.values?.questions[index]?.question}
														onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
															let inputValue = e.target.value;

															// Remove leading spaces if present
															inputValue = inputValue.replace(/^\s+/, '');

															// Allow only one space between words
															inputValue = inputValue.replace(/\s{2,}/g, ' ');
															handleQuestionChange(index, inputValue);
															formik.setFieldValue(`questions.${index}.question`, inputValue);
														}}
													/>
													{formik?.touched?.questions?.[index]?.question &&
														formik?.errors?.questions?.[index]?.question && (
															<div className="text-danger">
																{formik.errors?.questions[index]?.question}
															</div>
														)}
												</Form.Group>
											</div>
											{questions?.length !== 1 && (
												<Button
													className="edit-btn border-0 rounded-circle p-0"
													onClick={() => deleteQuestion(ques?.id)}
												>
													<i className="ri-delete-bin-7-line"></i>
												</Button>
											)}
											<CustomToggle eventKey={index.toString()}>
												<i className="ri-arrow-down-s-line"></i>
											</CustomToggle>
										</div>
									</Card.Header>
									<Accordion.Collapse eventKey={index.toString()}>
										<div className="create-questionnaire-options" id={`ques${index}`}>
											<Row>
												{ques?.options?.map((option, optIndex) => (
													<Col md={6} key={optIndex}>
														<div className="option-box">
															<Form.Group className="position-relative">
																<Form.Control
																	type="text"
																	placeholder={`${translate('Extras.writeOption')} ${optIndex + 1}`}
																	name={`questions.${index}.options.${optIndex}`}
																	className="rounded-pill"
																	autoComplete="off"
																	value={formik?.values?.questions[index]?.options[optIndex]}
																	onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																		let inputValue = e.target.value;

																		// Remove leading spaces if present
																		inputValue = inputValue.replace(/^\s+/, '');

																		// Allow only one space between words
																		inputValue = inputValue.replace(/\s{2,}/g, ' ');
																		formik.setFieldValue(
																			`questions.${index}.options.${optIndex}`,
																			inputValue,
																		);
																		handleOptionChange(index, optIndex, inputValue);
																	}}
																/>
																{formik?.touched.questions?.[index]?.options?.[optIndex] &&
																	formik.errors.questions?.[index]?.options?.[optIndex] && (
																		<div className="text-danger">
																			{formik.errors.questions[index].options[optIndex]}
																		</div>
																	)}
															</Form.Group>
														</div>
													</Col>
												))}
											</Row>
										</div>
									</Accordion.Collapse>
								</Card>
							))}
						</Accordion>
						<div className="d-flex justify-content-end gap-2 gap-md-3">
							<Button
								className="filled-btn rounded-pill"
								onClick={addQuestion}
								disabled={!formik.isValid || formik.values.questions.length === 10}
							>
								{translate('Questionnaire.addQuestion')}
							</Button>
							<Button className="filled-btn orange-bg rounded-pill px-5" type="submit">
								{translate('Questionnaire.publish')}
							</Button>
						</div>
					</Stack>
				</Form>
			</Container>
		</section>
	);
};

export default CreateQuestionnaire;
